import React, { useState, useEffect } from "react";
import { Combobox } from "@headlessui/react";
import { IIndustry } from '../../../types/types';
import { searchIndustries } from '../../../utils/server/industry';
import { useDebounce } from 'use-debounce';
type Props = {
  currentIndustry?: IIndustry | null;
  onSelect?: (industry: IIndustry | null) => void;
};

const IndustryCombobox: React.FC<Props> = ({ currentIndustry, onSelect }) => {
  const [all, setAll] = useState<IIndustry[]>([]);
  const [selected, setSelected] = useState<IIndustry | null>(currentIndustry ?? null);
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebounce(query, 300);
  const [loading, setLoading] = useState(false);

  // No initial fetch; only search as the user types

  // Server-side search when user types
  useEffect(() => {
    let cancelled = false;
    async function run() {
      const q = debouncedQuery.trim();
      if (!q) return; // keep initial 'all' for empty query
      setLoading(true);
      try {
        const results = await searchIndustries(q, { limit: 20 });
        if (!cancelled) setAll(results);
      } catch (e) {
        if (!cancelled) setAll([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  const filtered = all; // results already filtered server-side when query present

  return (
    <Combobox
      value={selected}
      onChange={(val: IIndustry) => {
        setSelected(val);
        onSelect?.(val);
      }}
      nullable
    >
      <Combobox.Label className="block text-sm font-medium text-gray-700">
        Industry
      </Combobox.Label>
      <Combobox.Input
        className="mt-1 w-full border rounded-md px-3 py-2"
        onChange={(e) => setQuery(e.target.value)}
        displayValue={(i: IIndustry) => i?.industry_name || ""}
        placeholder="Search industries..."
      />
      <Combobox.Options className="mt-1 max-h-60 overflow-auto border rounded-md bg-white">
        {loading && (
          <div className="px-4 py-2 text-gray-500">Searching…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="px-4 py-2 text-gray-500">No industries found</div>
        )}
        {filtered.map((i) => (
          <Combobox.Option key={i.id} value={i} className={({ active }) =>
            `cursor-pointer select-none px-4 py-2 ${
              active ? "bg-blue-600 text-white" : "text-gray-900"
            }`
          }>
            {i.industry_name} — <span className="text-gray-500">{i.company_type}</span>
          </Combobox.Option>
        ))}
      </Combobox.Options>
    </Combobox>
  );
}

export default IndustryCombobox;